import { useState } from "react";
import {
  useGetAdminRaisers,
  useGetAdminUpdates,
  useGetAdminContactMessages,
  useUpdateContactMessageStatus,
  useSendContactMessageReply,
  getGetAdminContactMessagesQueryKey,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "wouter";
import Navbar from "@/components/layout/Navbar";
import AdminApplications from "@/pages/admin/AdminApplications";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@workspace/ui-web/components/ui/card";
import { Badge } from "@workspace/ui-web/components/ui/badge";
import { Button } from "@workspace/ui-web/components/ui/button";
import { Textarea } from "@workspace/ui-web/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@workspace/ui-web/components/ui/select";
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from "@workspace/ui-web/components/ui/tabs";
import { Input } from "@workspace/ui-web/components/ui/input";
import { useToast } from "@workspace/ui-web/hooks/use-toast";
import {
  Loader2,
  Users,
  FileText,
  Dog,
  Clock,
  ChevronRight,
  Mail,
  Send,
  CornerDownRight,
  Search,
  ArrowUpDown,
} from "lucide-react";
import { format } from "date-fns";

type ContactStatus = "new" | "read" | "resolved";
type SortOrder = "newest" | "oldest";

const STATUS_LABELS: Record<ContactStatus, string> = {
  new: "New",
  read: "Read",
  resolved: "Resolved",
};

const STATUS_BADGE_VARIANT: Record<
  ContactStatus,
  "default" | "secondary" | "outline"
> = {
  new: "default",
  read: "secondary",
  resolved: "outline",
};

export default function AdminDashboard() {
  const { data: raisers, isLoading: raisersLoading } = useGetAdminRaisers();
  const { data: updates, isLoading: updatesLoading } = useGetAdminUpdates();
  const { data: contactMessages, isLoading: contactLoading } =
    useGetAdminContactMessages();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const updateStatus = useUpdateContactMessageStatus();
  const sendReply = useSendContactMessageReply();
  const [statusFilter, setStatusFilter] = useState<"all" | ContactStatus>(
    "all",
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [sortOrder, setSortOrder] = useState<SortOrder>("newest");
  const [replyDrafts, setReplyDrafts] = useState<Record<number, string>>({});
  const [replyingId, setReplyingId] = useState<number | null>(null);

  const handleStatusChange = (id: number, status: ContactStatus) => {
    updateStatus.mutate(
      { id, data: { status } },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: getGetAdminContactMessagesQueryKey(),
          });
          toast({
            title: "Status updated",
            description: `Message marked as ${STATUS_LABELS[status].toLowerCase()}.`,
          });
        },
        onError: () => {
          toast({
            title: "Error",
            description: "Failed to update status.",
            variant: "destructive",
          });
        },
      },
    );
  };

  const handleSendReply = (id: number) => {
    const body = (replyDrafts[id] ?? "").trim();
    if (!body) return;
    setReplyingId(id);
    sendReply.mutate(
      { id, data: { body } },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: getGetAdminContactMessagesQueryKey(),
          });
          setReplyDrafts((prev) => {
            const next = { ...prev };
            delete next[id];
            return next;
          });
          toast({
            title: "Reply sent",
            description:
              "Your reply was emailed and the message marked resolved.",
          });
        },
        onError: (error: unknown) => {
          const data =
            typeof error === "object" && error !== null && "data" in error
              ? (error as { data?: { error?: unknown } }).data
              : undefined;
          const serverMessage =
            typeof data?.error === "string" ? data.error : undefined;
          toast({
            title: "Could not send reply",
            description:
              serverMessage ??
              "The reply email failed to send. Please try again.",
            variant: "destructive",
          });
        },
        onSettled: () => {
          setReplyingId(null);
        },
      },
    );
  };

  if (raisersLoading || updatesLoading || contactLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-muted/20">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  const activeRaisers = raisers?.filter((r) => r.profile !== null) || [];

  return (
    <div className="min-h-screen flex flex-col bg-muted/20">
      <Navbar />
      <main className="flex-1 container px-4 py-8 mx-auto max-w-6xl">
        <div className="mb-8">
          <h1 className="text-3xl font-bold tracking-tight text-foreground">
            Admin Dashboard
          </h1>
          <p className="text-muted-foreground">
            Program overview and recent activity.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Users className="h-4 w-4" /> Total Users
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{raisers?.length || 0}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Dog className="h-4 w-4" /> Active Raisers (with dogs)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-primary">
                {activeRaisers.length}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <FileText className="h-4 w-4" /> Total Updates
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-secondary">
                {updates?.length || 0}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Raisers List */}
          <div className="space-y-4">
            <h2 className="text-xl font-bold flex items-center gap-2 border-b pb-2">
              <Users className="h-5 w-5" /> Raisers Directory
            </h2>
            <div className="bg-card rounded-xl border shadow-sm divide-y">
              {raisers?.length === 0 ? (
                <div className="p-6 text-center text-muted-foreground">
                  No raisers found.
                </div>
              ) : (
                raisers?.map((raiser) => (
                  <div
                    key={raiser.id}
                    className="p-4 flex items-center justify-between hover:bg-muted/50 transition-colors"
                  >
                    <div>
                      <div className="font-semibold text-foreground flex items-center gap-2">
                        {raiser.name || raiser.email}
                        {!raiser.profile && (
                          <Badge variant="secondary" className="text-[10px]">
                            No Profile
                          </Badge>
                        )}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {raiser.profile?.dogName
                          ? `Raising ${raiser.profile.dogName}`
                          : "Pending assignment"}
                      </div>
                    </div>
                    <Link href={`/admin/raisers/${raiser.id}`}>
                      <Button variant="ghost" size="sm" className="gap-1">
                        View <ChevronRight className="h-4 w-4" />
                      </Button>
                    </Link>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Recent Updates */}
          <div className="space-y-4">
            <h2 className="text-xl font-bold flex items-center gap-2 border-b pb-2">
              <Clock className="h-5 w-5" /> Recent Updates
            </h2>
            <div className="space-y-4">
              {updates?.length === 0 ? (
                <div className="p-6 text-center text-muted-foreground bg-card rounded-xl border">
                  No updates recorded yet.
                </div>
              ) : (
                updates?.slice(0, 10).map((update) => (
                  <Card key={update.id} className="overflow-hidden">
                    <CardHeader className="bg-muted/30 py-3 px-4 border-b">
                      <div className="flex justify-between items-center text-sm">
                        <span className="font-medium text-primary">
                          {update.raiser.name || update.raiser.email}
                          {update.raiser.dogName &&
                            ` • ${update.raiser.dogName}`}
                        </span>
                        <span className="text-muted-foreground">
                          {format(new Date(update.createdAt), "MMM d")}
                        </span>
                      </div>
                    </CardHeader>
                    <CardContent className="p-4">
                      <p className="text-sm text-foreground line-clamp-2 mb-2">
                        {update.note}
                      </p>
                      <div className="flex gap-2">
                        {update.milestones.length > 0 && (
                          <Badge variant="outline" className="bg-primary/5">
                            {update.milestones.length} milestones
                          </Badge>
                        )}
                        {update.photos.length > 0 && (
                          <Badge variant="outline" className="bg-secondary/5">
                            {update.photos.length} photos
                          </Badge>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Contact Messages */}
        <div className="mt-10 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b pb-2">
            <h2 className="text-xl font-bold flex items-center gap-2">
              <Mail className="h-5 w-5" /> Contact Messages
              {(() => {
                const newCount =
                  contactMessages?.filter((m) => m.status === "new").length ??
                  0;
                return newCount > 0 ? (
                  <Badge variant="default" className="ml-1">
                    {newCount} new
                  </Badge>
                ) : null;
              })()}
            </h2>
            {contactMessages?.length ? (
              <Tabs
                value={statusFilter}
                onValueChange={(v) =>
                  setStatusFilter(v as "all" | ContactStatus)
                }
              >
                <TabsList>
                  <TabsTrigger value="all">All</TabsTrigger>
                  <TabsTrigger value="new">New</TabsTrigger>
                  <TabsTrigger value="read">Read</TabsTrigger>
                  <TabsTrigger value="resolved">Resolved</TabsTrigger>
                </TabsList>
              </Tabs>
            ) : null}
          </div>
          {contactMessages?.length ? (
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder="Search by name, email, or subject..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select
                value={sortOrder}
                onValueChange={(v) => setSortOrder(v as SortOrder)}
              >
                <SelectTrigger className="w-full sm:w-[170px]">
                  <span className="flex items-center gap-2">
                    <ArrowUpDown className="h-4 w-4 text-muted-foreground" />
                    <SelectValue />
                  </span>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">Newest first</SelectItem>
                  <SelectItem value="oldest">Oldest first</SelectItem>
                </SelectContent>
              </Select>
            </div>
          ) : null}
          {(() => {
            const query = searchQuery.trim().toLowerCase();
            const filtered = (contactMessages ?? [])
              .filter(
                (msg) => statusFilter === "all" || msg.status === statusFilter,
              )
              .filter((msg) => {
                if (!query) return true;
                return (
                  msg.name.toLowerCase().includes(query) ||
                  msg.email.toLowerCase().includes(query) ||
                  (msg.subject?.toLowerCase().includes(query) ?? false)
                );
              })
              .sort((a, b) => {
                const diff =
                  new Date(b.createdAt).getTime() -
                  new Date(a.createdAt).getTime();
                return sortOrder === "newest" ? diff : -diff;
              });
            if (!contactMessages?.length) {
              return (
                <div className="p-6 text-center text-muted-foreground bg-card rounded-xl border">
                  No contact messages yet.
                </div>
              );
            }
            if (!filtered.length) {
              return (
                <div className="p-6 text-center text-muted-foreground bg-card rounded-xl border">
                  {query
                    ? `No messages match "${searchQuery.trim()}".`
                    : `No ${statusFilter} messages.`}
                </div>
              );
            }
            return (
              <div className="space-y-4">
                {filtered.map((msg) => {
                  const status = msg.status as ContactStatus;
                  return (
                    <Card
                      key={msg.id}
                      className={`overflow-hidden ${status === "new" ? "border-primary/40 shadow-sm" : ""}`}
                    >
                      <CardHeader className="bg-muted/30 py-3 px-4 border-b">
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 text-sm">
                          <div className="font-medium text-primary flex items-center gap-2 flex-wrap">
                            <Badge
                              variant={STATUS_BADGE_VARIANT[status]}
                              className="text-[10px] uppercase tracking-wide"
                            >
                              {STATUS_LABELS[status]}
                            </Badge>
                            <span>
                              {msg.name}
                              <span className="text-muted-foreground font-normal">
                                {" "}
                                &lt;{msg.email}&gt;
                              </span>
                              {msg.phone && (
                                <span className="text-muted-foreground font-normal">
                                  {" "}
                                  &middot; {msg.phone}
                                </span>
                              )}
                            </span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-muted-foreground">
                              {format(new Date(msg.createdAt), "MMM d, yyyy")}
                            </span>
                            <Select
                              value={status}
                              onValueChange={(v) =>
                                handleStatusChange(msg.id, v as ContactStatus)
                              }
                              disabled={updateStatus.isPending}
                            >
                              <SelectTrigger className="h-8 w-[130px]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="new">New</SelectItem>
                                <SelectItem value="read">Read</SelectItem>
                                <SelectItem value="resolved">
                                  Resolved
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="p-4 space-y-3">
                        {msg.subject && (
                          <div className="font-semibold text-foreground">
                            {msg.subject}
                          </div>
                        )}
                        <p className="text-sm text-foreground whitespace-pre-wrap">
                          {msg.message}
                        </p>

                        {msg.replies && msg.replies.length > 0 && (
                          <div className="space-y-2 border-l-2 border-primary/30 pl-3">
                            {msg.replies.map((reply) => (
                              <div
                                key={reply.id}
                                className="rounded-md bg-muted/40 p-3"
                              >
                                <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                                  <CornerDownRight className="h-3.5 w-3.5" />
                                  <span>
                                    Replied to {reply.sentToEmail} &middot;{" "}
                                    {format(
                                      new Date(reply.createdAt),
                                      "MMM d, yyyy 'at' h:mm a",
                                    )}
                                  </span>
                                </div>
                                <p className="text-sm text-foreground whitespace-pre-wrap">
                                  {reply.body}
                                </p>
                              </div>
                            ))}
                          </div>
                        )}

                        <div className="pt-1 space-y-2">
                          <Textarea
                            value={replyDrafts[msg.id] ?? ""}
                            onChange={(e) =>
                              setReplyDrafts((prev) => ({
                                ...prev,
                                [msg.id]: e.target.value,
                              }))
                            }
                            placeholder={`Write a reply to ${msg.name}…`}
                            rows={3}
                            disabled={
                              replyingId === msg.id && sendReply.isPending
                            }
                            className="resize-y"
                          />
                          <div className="flex justify-end">
                            <Button
                              size="sm"
                              className="gap-1.5"
                              onClick={() => handleSendReply(msg.id)}
                              disabled={
                                !(replyDrafts[msg.id] ?? "").trim() ||
                                (replyingId === msg.id && sendReply.isPending)
                              }
                            >
                              {replyingId === msg.id && sendReply.isPending ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Send className="h-4 w-4" />
                              )}
                              Send reply
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            );
          })()}

          <AdminApplications />
        </div>
      </main>
    </div>
  );
}
